import { useState, useMemo, useEffect } from 'react'
import { X, CirclePlus, CircleMinus, Plus, User, Users } from '@/components/icons'
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer'
import { Avatar, AvatarFallback } from './ui/avatar'
import { SearchInput } from './ui/search-input'
import { Button } from './ui/button'
import { usePassengerGetAll } from '../lib/hooks/passenger'
import { ListSkeleton } from './ui/skeleton-presets'
import { cn, getInitials } from '../lib/utils'
import { EmptyState } from './EmptyState'
import { PaxDrawer } from './PaxDrawer'

interface Passenger {
  id: string
  firstName: string | null
  lastName: string | null
  role: string | null
  avatarColor?: string | null
}

interface PassengerPickerDrawerBaseProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  excludePassengerIds?: string[]
  availablePassengers?: Passenger[]
  isLoading?: boolean
}

interface SingleSelectProps extends PassengerPickerDrawerBaseProps {
  mode?: 'single'
  onSelect: (passengerId: string) => void
  selectedPassengerId?: string
  selectedPassengerIds?: never
  onSelectMultiple?: never
}

interface MultiSelectProps extends PassengerPickerDrawerBaseProps {
  mode: 'multiple'
  /** Second arg is present only when the unknown-PAX stepper is enabled. */
  onSelectMultiple: (passengerIds: string[], unknownPaxCount?: number) => void
  selectedPassengerIds?: string[]
  /** Enables the "Add unknown PAX" stepper row; value seeds the draft on open. */
  unknownPaxCount?: number
  onSelect?: never
  selectedPassengerId?: never
}

type PassengerPickerDrawerProps = SingleSelectProps | MultiSelectProps

/**
 * "Add unknown PAX" stepper row (Figma 12674-66611) — shared between the
 * FlightDrawer create wizard and the multi-select passenger picker.
 */
export function UnknownPaxRow({
  count,
  onChange,
}: {
  count: number
  onChange: (count: number) => void
}) {
  return (
    <div className="flex w-full items-center gap-3 rounded-lg bg-surface-L2 px-2 py-2">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-content-primary-subtle-mid">
        <User className="size-5 text-content-primary-inverse" />
      </div>
      <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-content-primary-bold">
        Add unknown PAX
      </span>
      <button
        type="button"
        aria-label="Remove unknown passenger"
        disabled={count === 0}
        onClick={() => onChange(Math.max(0, count - 1))}
        className={cn('shrink-0 p-1 active:opacity-70', count === 0 && 'opacity-40')}
      >
        <CircleMinus className="size-5 text-content-brand-bold" />
      </button>
      <span className="min-w-4 shrink-0 text-center text-sm font-semibold text-content-primary-bold">
        {count}
      </span>
      <button
        type="button"
        aria-label="Add unknown passenger"
        onClick={() => onChange(count + 1)}
        className="shrink-0 p-1 active:opacity-70"
      >
        <CirclePlus className="size-5 text-content-brand-bold" />
      </button>
    </div>
  )
}

export function PassengerPickerDrawer(props: PassengerPickerDrawerProps) {
  const {
    isOpen,
    onClose,
    title,
    excludePassengerIds = [],
    availablePassengers,
    mode = 'single',
  } = props

  const [searchQuery, setSearchQuery] = useState('')
  const [isPaxDrawerOpen, setIsPaxDrawerOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(props.mode === 'multiple' ? props.selectedPassengerIds || [] : [])
  )
  // Whether the unknown-PAX stepper is enabled (multiple mode opt-in via prop).
  const showUnknownPax = props.mode === 'multiple' && props.unknownPaxCount !== undefined
  // Draft unknown-PAX count — committed together with the selection on Select.
  const [draftUnknownCount, setDraftUnknownCount] = useState(0)

  // Sync selection state when drawer opens
  useEffect(() => {
    if (isOpen && props.mode === 'multiple') {
      setSelectedIds(new Set(props.selectedPassengerIds || []))
      setDraftUnknownCount(props.unknownPaxCount ?? 0)
    }
  }, [isOpen])

  // Fetch passengers only when availablePassengers is not provided
  const { data: fetchedPassengers = [], isPending: fetchLoading } = usePassengerGetAll()
  const loading = props.isLoading ?? fetchLoading

  const passengers = useMemo(() => {
    if (availablePassengers) return availablePassengers
    return fetchedPassengers
  }, [availablePassengers, fetchedPassengers])

  // Filter passengers by search query and exclusions
  const filteredPassengers = useMemo(() => {
    let filtered = passengers

    // Filter out excluded passengers
    if (excludePassengerIds.length > 0) {
      filtered = filtered.filter((p) => !excludePassengerIds.includes(p.id))
    }

    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (p) =>
          p.firstName?.toLowerCase().includes(searchLower) ||
          p.lastName?.toLowerCase().includes(searchLower) ||
          (p.role && p.role.toLowerCase().includes(searchLower))
      )
    }

    return filtered
  }, [passengers, searchQuery, excludePassengerIds])

  const handleSingleSelect = (passengerId: string) => {
    if (props.mode !== 'multiple' && props.onSelect) {
      props.onSelect(passengerId)
      setSearchQuery('')
      onClose()
    }
  }

  const toggleSelection = (passengerId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(passengerId)) {
        newSet.delete(passengerId)
      } else {
        newSet.add(passengerId)
      }
      return newSet
    })
  }

  const handleSelect = () => {
    if (props.mode === 'multiple' && props.onSelectMultiple) {
      props.onSelectMultiple(
        Array.from(selectedIds),
        showUnknownPax ? draftUnknownCount : undefined
      )
    }
    setSearchQuery('')
    onClose()
  }

  const handleClose = () => {
    setSearchQuery('')
    if (props.mode === 'multiple') {
      setSelectedIds(new Set(props.selectedPassengerIds || []))
      setDraftUnknownCount(props.unknownPaxCount ?? 0)
    }
    onClose()
  }

  const getTitle = () => {
    if (title) return title
    return mode === 'multiple' ? 'Select Passengers' : 'Passenger'
  }

  const isSingleMode = mode === 'single'
  const selectedPassengerId = isSingleMode ? (props as SingleSelectProps).selectedPassengerId : undefined

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 shadow-lg max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {getTitle()}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {!loading && passengers.length === 0 ? (
          <div className="px-4 py-6 flex flex-col flex-1">
            <EmptyState
              icon={Users}
              title="No passengers yet"
              description="Add your first passenger before selecting."
              addButtonText="Add Passenger"
              onAdd={() => setIsPaxDrawerOpen(true)}
            />
          </div>
        ) : (
        <>
        {/* Content */}
        <div className="min-h-0 flex-1 overflow-hidden px-2">
          <div className="flex flex-col gap-2 p-2 h-full">
            {/* Search Input */}
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search passengers"
            />

            {/* Selected count + brand "Add New" button (Figma 12674-66611) */}
            <div className="flex items-center justify-between min-h-8">
              <span className="text-xs font-semibold text-content-primary-subtle">
                {!isSingleMode
                  ? `${selectedIds.size + draftUnknownCount} selected`
                  : ''}
              </span>
              <button
                type="button"
                onClick={() => setIsPaxDrawerOpen(true)}
                className="inline-flex items-center gap-1.5 p-3 -mr-3 text-xs font-semibold text-content-brand-bold active:opacity-70"
              >
                <Plus className="size-3" />
                Add New
              </button>
            </div>

            {/* Unknown PAX stepper (multiple mode opt-in) */}
            {showUnknownPax && (
              <UnknownPaxRow count={draftUnknownCount} onChange={setDraftUnknownCount} />
            )}

            {/* Passenger List */}
            <div
              className="overflow-y-auto overscroll-contain h-[300px]"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {loading ? (
                <ListSkeleton />
              ) : filteredPassengers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <p className="text-sm text-content-primary-subtle text-center">
                    {searchQuery
                      ? 'No passengers found matching your search'
                      : 'No passengers available'}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {filteredPassengers.map((passenger, index) => {
                    const isSelectedSingle = selectedPassengerId === passenger.id
                    const isSelectedMulti = selectedIds.has(passenger.id)
                    const isLast = index === filteredPassengers.length - 1
                    const initials = getInitials(passenger.firstName, passenger.lastName)
                    const displayName =
                      passenger.firstName && passenger.lastName
                        ? `${passenger.firstName} ${passenger.lastName}`
                        : passenger.firstName || passenger.lastName || ''

                    if (isSingleMode) {
                      return (
                        <div key={passenger.id}>
                          <button
                            type="button"
                            onClick={() => handleSingleSelect(passenger.id)}
                            className={`w-full flex items-center gap-3 min-h-9 px-3 py-2 rounded-md transition-colors ${
                              isSelectedSingle
                                ? 'bg-surface-L2'
                                : 'hover:bg-surface-L2/50'
                            }`}
                          >
                            <Avatar className="size-8 shrink-0"><AvatarFallback className="text-background" style={passenger.avatarColor ? { backgroundColor: passenger.avatarColor } : undefined}>{initials}</AvatarFallback></Avatar>
                            <div className="flex flex-1 flex-col items-start justify-center">
                              <span className="text-sm leading-normal tracking-[0.07px] text-content-primary-bold">
                                {displayName}
                              </span>
                              <span className="text-xs leading-normal tracking-[0.18px] text-content-primary-subtle">
                                {passenger.role === 'Primary' ? 'Primary' : 'Guest'}
                              </span>
                            </div>
                            {isSelectedSingle && (
                              <CircleMinus className="size-5 text-content-primary-subtle shrink-0" />
                            )}
                          </button>
                          {!isLast && <div className="h-px bg-border-L1 mx-3" />}
                        </div>
                      )
                    }

                    // Multiple selection mode
                    return (
                      <div key={passenger.id}>
                        <button
                          type="button"
                          onClick={() => toggleSelection(passenger.id)}
                          className={`w-full flex items-center gap-3 min-h-9 px-3 py-2 rounded-md transition-colors ${
                            isSelectedMulti
                              ? 'bg-surface-L2'
                              : 'hover:bg-surface-L2/50'
                          }`}
                        >
                          <Avatar className="size-8 shrink-0"><AvatarFallback className="text-background" style={passenger.avatarColor ? { backgroundColor: passenger.avatarColor } : undefined}>{initials}</AvatarFallback></Avatar>
                          <div className="flex flex-1 flex-col items-start justify-center">
                            <span className="text-sm leading-normal tracking-[0.07px] text-content-primary-bold">
                              {displayName}
                            </span>
                            <span className="text-xs leading-normal tracking-[0.18px] text-content-primary-subtle">
                              {passenger.role === 'Primary' ? 'Primary' : 'Guest'}
                            </span>
                          </div>
                          {isSelectedMulti ? (
                            <CircleMinus className="size-5 shrink-0 text-content-brand-bold" />
                          ) : (
                            <CirclePlus className="size-5 shrink-0 text-content-brand-bold" />
                          )}
                        </button>
                        {!isLast && <div className="h-px bg-border-L1 mx-3" />}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer for multiple selection mode */}
        {!isSingleMode && (
          <DrawerFooter className="border-t flex-row">
            <Button variant="secondary" size="mini" className="flex-1" onClick={handleClose}>
              Cancel
            </Button>
            <Button size="mini" className="flex-1" onClick={handleSelect}>
              Select
            </Button>
          </DrawerFooter>
        )}
        </>
        )}
      </DrawerContent>
    </Drawer>

    {/* Add Passenger Drawer — opens from the empty state or the add row */}
    <PaxDrawer
      isOpen={isPaxDrawerOpen}
      onClose={() => setIsPaxDrawerOpen(false)}
    />
    </>
  )
}
